import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Truck, EnvelopeSimple, Lock } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

const formatApiErrorDetail = (detail) => {
  if (detail == null) return 'Algo salió mal. Por favor, inténtalo de nuevo.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === 'string' ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(' ');
  if (detail && typeof detail.msg === 'string') return detail.msg;
  return String(detail);
};

export const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const user = await login(email, password);
      if (user.role === 'admin') {
        navigate('/admin');
      } else if (user.role === 'coordinador') {
        navigate('/coordinador');
      } else {
        navigate('/dashboard');
      }
    } catch (err) {
      setError(
        formatApiErrorDetail(err.response?.data?.detail) || err.message
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FFFFFF] flex">
      {/* Left side - Login Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-[#C1121F] flex items-center justify-center">
                <Truck size={28} weight="bold" className="text-white" />
              </div>
              <h1 className="text-3xl font-black font-['Chivo'] tracking-tight text-[#0A0A0A]">
                Gestión de Movilizaciones CRE JPP
              </h1>
            </div>
            <p className="text-[#495057] font-['IBM_Plex_Sans'] text-base">
              Ingresa tus credenciales para acceder al sistema
            </p>
          </div>

          <form data-testid="login-form" onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="email" className="font-['IBM_Plex_Sans'] font-medium text-[#0A0A0A]">
                Correo Electrónico
              </Label>
              <div className="relative">
                <EnvelopeSimple
                  size={20}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-[#495057]"
                  weight="bold"
                />
                <Input
                  id="email"
                  data-testid="email-input"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-11 h-12 border-[#E9ECEF] focus:border-[#C1121F] font-['IBM_Plex_Sans']"
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="font-['IBM_Plex_Sans'] font-medium text-[#0A0A0A]">
                Contraseña
              </Label>
              <div className="relative">
                <Lock
                  size={20}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-[#495057]"
                  weight="bold"
                />
                <Input
                  id="password"
                  data-testid="password-input"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-11 h-12 border-[#E9ECEF] focus:border-[#C1121F] font-['IBM_Plex_Sans']"
                  required
                />
              </div>
            </div>

            {error && (
              <div
                data-testid="login-error"
                className="p-4 bg-[#FF3B30]/10 border border-[#FF3B30] text-[#FF3B30] font-['IBM_Plex_Sans'] text-sm"
              >
                {error}
              </div>
            )}

            <Button
              data-testid="login-submit-button"
              type="submit"
              disabled={loading}
              className="w-full h-12 bg-[#C1121F] hover:bg-[#8B0000] text-white font-['IBM_Plex_Sans'] font-medium transition-colors"
            >
              {loading ? 'Iniciando sesión...' : 'Iniciar Sesión'}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-[#495057] font-['IBM_Plex_Sans'] text-sm">
              ¿No tienes una cuenta?{' '}
              <Link
                to="/register"
                data-testid="register-link"
                className="text-[#C1121F] font-medium hover:underline transition-colors"
              >
                Regístrate aquí
              </Link>
            </p>
          </div>
        </div>
      </div>

      {/* Right side - Image */}
      <div className="hidden lg:block lg:w-1/2 relative">
        <img
          src="/images/hero-vehiculos.jpg"
          alt="Vehículos Cruz Roja Ecuatoriana Junta Provincial Pichincha"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-[#C1121F]/20"></div>
      </div>
    </div>
  );
};